import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Progress } from '../ui/progress'
import { BarChart3, MessageSquare, Send, Users } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import {
  gatewayStatsQueryOptions,
  devicesQueryOptions,
  profileQueryOptions,
} from '@/lib/queries'

export default function UsageTracking() {
  // Fetch usage data from API
  const { data: statsData } = useQuery(gatewayStatsQueryOptions())

  const { data: deviceData = [] } = useQuery({
    ...devicesQueryOptions(),
    select: (data) => (Array.isArray(data) ? data : []),
  })

  const { data: profileData } = useQuery(profileQueryOptions())

  const devices = Array.isArray(deviceData) ? deviceData : []
  const devicesConnected = devices.filter((device: any) => device.enabled).length
  
  // Get limits from user profile/plan or use defaults
  const messagesLimit = profileData?.plan?.messagesLimit || 1000
  const devicesLimit = profileData?.plan?.devicesLimit || 5
  const apiCallsLimit = profileData?.plan?.apiCallsLimit || 10000

  const usage = {
    messagesSent: statsData?.sentSMS || 0,
    messagesLimit,
    devicesConnected,
    devicesLimit,
    apiCalls: statsData?.apiCalls || 0,
    apiCallsLimit,
  }

  const messagesPercentage = (usage.messagesSent / usage.messagesLimit) * 100
  const apiCallsPercentage = (usage.apiCalls / usage.apiCallsLimit) * 100

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5" />
          Usage Overview
        </CardTitle>
        <CardDescription>
          Monitor your current usage and limits
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-blue-500" />
                <span className="text-sm font-medium">SMS Messages</span>
              </div>
              <span className="text-sm text-muted-foreground">
                {usage.messagesSent} / {usage.messagesLimit}
              </span>
            </div>
            <Progress value={messagesPercentage} className="h-2" />
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Send className="h-4 w-4 text-green-500" />
                <span className="text-sm font-medium">API Calls</span>
              </div>
              <span className="text-sm text-muted-foreground">
                {usage.apiCalls} / {usage.apiCallsLimit}
              </span>
            </div>
            <Progress value={apiCallsPercentage} className="h-2" />
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-purple-500" />
                <span className="text-sm font-medium">Devices</span>
              </div>
              <span className="text-sm text-muted-foreground">
                {usage.devicesConnected} / {usage.devicesLimit}
              </span>
            </div>
            <Progress value={(usage.devicesConnected / usage.devicesLimit) * 100} className="h-2" />
          </div>
        </div>
        
        <div className="text-xs text-muted-foreground text-center">
          Usage resets monthly. Upgrade your plan for higher limits.
        </div>
      </CardContent>
    </Card>
  )
}
